import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { AdminSubscriptionCollectionItemResponse } from '../responses/admin-subscription-collection-item.response';
import { AdminSubscriptionDetailsResponse } from '../responses/admin-subscription-details.response';
import { AdminSubscriptionOrderCollectionItemResponse } from '../responses/admin-subscription-order-collection-item.response';
import { AdminSubscriptionBillingAddressResponse } from '../responses/admin-subscription-billing-address.response';
import { AdminSubscriptionBillingAddressUpdateRequest } from '../requests/admin-subscription-billing-address-update.request';
import { AdminSubscriptionShippingAddressUpdateRequest } from '../requests/admin-subscription-shipping-address-update.request';
import { AdminSubscriptionShippingAddressResponse } from '../responses/admin-subscription-shipping-address.response';
import { AdminSubscriptionShippingMethodCollectionItemResponse } from '../responses/admin-subscription-shipping-method-collection-item.response';
import { AdminSubscriptionsToProcessDetailsResponse } from '../responses/admin-subscriptions-to-process-details.response';
import { AdminSubscriptionUpdateRequest } from '../responses/admin-subscription-update.request';
import { AdminSubscriptionBulkPriceChangeRequest } from '../responses/admin-subscription-bulk-price-change.request';
import { AdminSubscriptionsBulkPriceChangeDetailsResponse } from '../responses/admin-subscriptions-bulk-price-change-details.response';

@Injectable({
  providedIn: 'root',
})
export class AdminSubscriptionApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public updateBillingAddress(param: {
    id: Guid;
    request: AdminSubscriptionBillingAddressUpdateRequest;
  }): Observable<void> {
    const { id, request } = param;
    const path = `admin/subscription/${HttpParams.Id}/billing`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminSubscriptionBillingAddressUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public updateSubscription(param: {
    id: Guid;
    request: AdminSubscriptionUpdateRequest;
  }): Observable<void> {
    const { id, request } = param;
    const path = `admin/subscription/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminSubscriptionUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public updateShippingAddress(param: {
    id: Guid;
    request: AdminSubscriptionShippingAddressUpdateRequest;
  }): Observable<void> {
    const { id, request } = param;
    const path = `admin/subscription/${HttpParams.Id}/shipping`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminSubscriptionShippingAddressUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public fetchBillingAddressDetails(
    id: Guid,
  ): Observable<AdminSubscriptionBillingAddressResponse> {
    const path = `admin/subscription/${HttpParams.Id}/billing`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminSubscriptionBillingAddressResponse,
          ),
        ),
      );
  }

  public fetchShippingAddressDetails(
    id: Guid,
  ): Observable<AdminSubscriptionShippingAddressResponse> {
    const path = `admin/subscription/${HttpParams.Id}/shipping`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminSubscriptionShippingAddressResponse,
          ),
        ),
      );
  }

  public fetchCollection(options: {
    cacheable?: boolean;
    request: CollectionRequest;
  }): Observable<CollectionResponse<AdminSubscriptionCollectionItemResponse>> {
    const path = `admin/subscription`;
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminSubscriptionCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchSubscriptionOrderCollection(params: {
    request: CollectionRequest;
    id: Guid;
  }): Observable<
    CollectionResponse<AdminSubscriptionOrderCollectionItemResponse>
  > {
    const path = `admin/subscription/${HttpParams.Id}/order`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.Id]: params.id })
        .queryParams(
          this.provider.httpHelper.collectionRequestToQueryParams(
            params.request,
          ),
        )
        .build(),
    );

    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminSubscriptionOrderCollectionItemResponse,
          ),
        ),
      );
  }

  public cancelById(id: Guid): Observable<void> {
    const path = `admin/subscription/${HttpParams.Id}/cancel`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public suspendById(id: Guid): Observable<void> {
    const path = `admin/subscription/${HttpParams.Id}/suspend`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public resumeById(id: Guid): Observable<void> {
    const path = `admin/subscription/${HttpParams.Id}/resume`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public processById(id: Guid): Observable<void> {
    const path = `admin/subscription/${HttpParams.Id}/process`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public processAvailable(): Observable<void> {
    const path = `admin/subscription/bulk/process`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public fetchSubscriptionsToProcessDetails(): Observable<AdminSubscriptionsToProcessDetailsResponse> {
    const path = `admin/subscription/bulk/process`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminSubscriptionsToProcessDetailsResponse,
          ),
        ),
      );
  }

  public fetchDetailsById(options: {
    id: Guid;
  }): Observable<AdminSubscriptionDetailsResponse> {
    const { id } = options;
    const path = `admin/subscription/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminSubscriptionDetailsResponse,
          ),
        ),
      );
  }

  public fetchAvailableShippingMethodCollection(params: {
    cacheable?: boolean;
    countryId?: Nillable<Guid>;
    subscriptionId: Guid;
    skipByLoading: boolean;
    request: CollectionRequest;
  }): Observable<
    CollectionResponse<AdminSubscriptionShippingMethodCollectionItemResponse>
  > {
    const path = `admin/subscription/${HttpParams.Id}/shipping/method`;
    let queryParams = this.provider.httpHelper.collectionRequestToQueryParams(
      params.request,
    );

    if (!isNil(params.countryId)) {
      queryParams = { ...queryParams, countryId: params.countryId };
    }

    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .queryParams(queryParams)
        .params({
          [HttpParams.Id]: params.subscriptionId,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(params?.cacheable, false),
      )
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(params?.skipByLoading, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminSubscriptionShippingMethodCollectionItemResponse,
          ),
        ),
      );
  }

  public bulkPriceChange(
    request: AdminSubscriptionBulkPriceChangeRequest,
  ): Observable<void> {
    const path = `admin/subscription/bulk/price-change`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder.build();

    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminSubscriptionBulkPriceChangeRequest,
    );

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public fetchBulkPriceChangeDetails(): Observable<AdminSubscriptionsBulkPriceChangeDetailsResponse> {
    const path = `admin/subscription/bulk/price-change`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminSubscriptionsBulkPriceChangeDetailsResponse,
          ),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
